// MultiSelect.js — Seletor de múltiplos itens com checkboxes, busca e range.
import React, { useState, useEffect } from 'react';
import './MultiSelect.css';

function expandRange(csv) {
  const result = new Set();
  if (!csv) return result;
  String(csv).split(',').forEach((part) => {
    const p = part.trim();
    if (!p) return;
    const m = p.match(/^(\d+)-(\d+)$/);
    if (m) {
      const a = Number(m[1]);
      const b = Number(m[2]);
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      for (let i = lo; i <= hi; i++) result.add(String(i));
    } else {
      result.add(p);
    }
  });
  return result;
}

function setToCsv(set) {
  return Array.from(set).sort((a, b) => Number(a) - Number(b)).join(', ');
}

function MultiSelect({ serverId, value, onChange, fetchItems, itemKey, itemLabel, title, emptyText }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [searchText, setSearchText] = useState('');
  const [rangeText, setRangeText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSelected(expandRange(value || ''));
  }, [value]);

  useEffect(() => {
    if (!serverId) {
      setItems([]);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    fetchItems(serverId)
      .then(setItems)
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [serverId, fetchItems]);

  const filteredItems = items.filter((i) => {
    if (!searchText) return true;
    const k = String(itemKey(i));
    const l = String(itemLabel(i));
    return k.includes(searchText) || l.toLowerCase().includes(searchText.toLowerCase());
  });

  const commit = (next) => {
    setSelected(next);
    onChange(setToCsv(next));
  };

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    commit(next);
  };

  const selectFiltered = () => {
    const next = new Set(selected);
    filteredItems.forEach((i) => next.add(itemKey(i)));
    commit(next);
  };

  const clearFiltered = () => {
    const next = new Set(selected);
    filteredItems.forEach((i) => next.delete(itemKey(i)));
    commit(next);
  };

  const clearAll = () => commit(new Set());

  const addRange = () => {
    if (!rangeText.trim()) return;
    const r = expandRange(rangeText);
    const next = new Set(selected);
    r.forEach((v) => next.add(v));
    commit(next);
    setRangeText('');
  };

  return (
    <div className="multiSelect">
      <div className="msHeader">
        <span className="msTitle">
          {title} <span className="msCount">({selected.size} selecionado{selected.size !== 1 ? 's' : ''})</span>
        </span>
      </div>

      <div className="msToolbar">
        <input
          type="text"
          className="msSearch"
          placeholder="Buscar..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="msRange">
          <input
            type="text"
            className="msRangeInput"
            placeholder="Ex: 1000-1019"
            value={rangeText}
            onChange={(e) => setRangeText(e.target.value)}
          />
          <button type="button" className="msBtn" onClick={addRange}>Adicionar range</button>
        </div>
      </div>

      <div className="msActions">
        <button type="button" className="msBtn" onClick={selectFiltered}>Marcar visíveis</button>
        <button type="button" className="msBtn" onClick={clearFiltered}>Desmarcar visíveis</button>
        <button type="button" className="msBtn msBtnDanger" onClick={clearAll}>Limpar tudo</button>
      </div>

      {loading && <div className="msLoading">Carregando...</div>}
      {error && <div className="msError">{error}</div>}

      {!loading && !error && (
        <div className="msList">
          {filteredItems.map((i) => {
            const k = itemKey(i);
            return (
              <label key={k} className="msItem">
                <input type="checkbox" checked={selected.has(k)} onChange={() => toggle(k)} />
                <span className="msItemLabel">{itemLabel(i)}</span>
              </label>
            );
          })}
          {filteredItems.length === 0 && <div className="msEmpty">{emptyText || 'Nenhum item.'}</div>}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
